import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { hasFileChanges } from '@risksmart-app/components/src/file/fileUtils';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import {
  getContributors,
  getOwners,
  getViewers,
} from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useUpdateAction } from '@/hooks/mutations/action';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetActionById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import ActionForm from '../../forms/ActionForm';
import type { ActionFormFieldData } from '../../forms/actionsSchema';
import { defaultValues } from '../../forms/actionsSchema';

const Tab: FC = () => {
  useI18NSummaryHelpContent('actions.help');
  const { updateFiles } = useFileUpdate();
  const { t } = useTranslation(['common']);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Action is the pilot register for AI form prefill (AID-382): scalar-heavy,
  // clean shared config. Widening to another register is this one prop at that
  // form's call site — nothing here is Action-specific.
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const navigate = useNavigate();
  const actionId = useGetGuidParam('actionId');
  const {
    data,
    error,
    loading: loadingAction,
  } = useGetActionById({ queryArgs: { id: actionId } });
  if (error) {
    throw error;
  }

  if (data?.action.length === 0) {
    throw new PageNotFound(`Action with id ${actionId} not found`);
  }
  const action = data?.action[0];
  const { hasPermission: canEditAction, loading: isLoadingEditAction } =
    useHasPermissionQuery('update:action', action);
  const values = {
    ...defaultValues,
    ...action,
    files: action?.files.map((rf) => rf.file),
    Owners: getOwners(action),
    Contributors: getContributors(action),
    Viewers: getViewers(action),
    ancestorContributors: action?.ancestorContributors ?? [],
  };
  const { updateAction } = useUpdateAction();

  const onDismiss = () => {
    navigate(-1);
  };

  const onSave = async (
    data: ActionFormFieldData,
    confirmChangeRequest?: boolean
  ) => {
    const { files } = data;

    if (!action) {
      throw new Error('action missing');
    }

    const ids = ownerContributorAndViewerIds(data);

    await updateAction(
      {
        ...data,
        Priority: data.Priority,
        OriginalTimestamp: action.ModifiedAtTimestamp,
        Id: action.Id,
        CustomAttributeData: data.CustomAttributeData || undefined,
        DepartmentTypeIds:
          data.departments?.map((d) => d.DepartmentTypeId) || [],
        TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
        ...ids,
      },
      {
        confirmChangeRequest,
        hasFileChanges: hasFileChanges(
          values?.files?.map((file) => ({ Id: file?.Id })),
          files
        ),
      }
    );
    await updateFiles({
      parentType: Parent_Type_Enum.Action,
      parentId: action?.Id,
      originalFiles: values?.files,
      selectedFiles: files,
    });
  };

  // The record read gates editing just as the permission check does (ENG-616).
  //
  // `values` is handed to react-hook-form as its `values` prop, so it RESETS the
  // form whenever it changes. While the by-id read is in flight `action` is
  // undefined and `values` is just `defaultValues`; when the read lands, RHF
  // resets and silently discards anything typed in the meantime. Every other
  // detail tab already folds its record read into `readOnly` for this reason
  // (assessments, internal-audit, third-party, policy, risks) — this one did not,
  // and it is the V3 pack's most persistent flake: `approvals.spec.ts` filled
  // targetCloseDate ~100ms into a 665ms `actionById`, saved against a
  // defaults-only baseline so no change request was raised and no approval modal
  // opened, then watched the field revert to its original value.
  const readOnly = loadingAction || isLoadingEditAction || !canEditAction;

  return (
    <ActionForm
      values={values as ActionFormFieldData}
      onDismiss={onDismiss}
      onSave={onSave}
      readOnly={readOnly}
      header={t('details')}
      approvalConfig={{ object: { Id: actionId } }}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      activeFormRecordId={actionId}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default Tab;
